import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Star, Copy, FileDown, X, Ruler, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  FLANGES, searchFlanges, fmtDual, getFlangeFavs, toggleFlangeFav,
} from "@/lib/flangeFinderData";
import { FlangeDiagram } from "@/components/FlangeDiagram";

class FinderErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="card-rs p-5 text-center" data-testid="ff-page-error">
          <p className="text-[13px] font-semibold mb-1">Something went wrong loading the Flange Finder.</p>
          <p className="text-[12px] text-[var(--rs-text-3)] mb-4">Your data is safe — try again or go back to Calculators.</p>
          <a href="/categories" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-md font-semibold text-[13px]"
            style={{ background: "var(--rs-amber)", color: "#141419" }} data-testid="ff-page-error-back">
            <ArrowLeft className="w-4 h-4" /> Back to Calculators
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

function copyText(text, label) {
  const done = () => toast.success(`${label} copied`);
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(() => {});
  } else {
    const ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    document.execCommand("copy"); document.body.removeChild(ta); done();
  }
}

export function buildSpecRows(f, metric) {
  const gasket = f.gasket ? `${f.gasket.odIn.toFixed(3)}" × ${f.gasket.wIn.toFixed(3)}"` : "see gasket table";
  return [
    ["Nominal Flange Size", `${f.size} (Type ${f.type})`],
    ["Max Service Pressure", `${f.psi.toLocaleString()} psi (${f.bar} bar)`],
    [f.type === "6BX" ? "Pressure-Energized Ring (BX)" : "Standard Ring Gasket (R/RX)", f.ringLabel],
    ["Ring OD × Width", gasket],
    ["Outside Diameter (OD)", fmtDual(f.odMm, metric)],
    ["Flange Thickness", fmtDual(f.thkMm, metric)],
    ["Hub Outside Diameter", fmtDual(f.hubMm, metric)],
    [f.type === "6BX" ? "Groove OD" : "Groove Pitch Diameter", fmtDual(f.type === "6BX" ? f.grvOdMm : f.grvPitchMm, metric)],
    ["Groove Inside Diameter", fmtDual(f.grvIdMm, metric)],
    ["Groove Width", fmtDual(f.grvWidthMm, metric, 3)],
    ["Groove Depth", fmtDual(f.grvDepthMm, metric, 3)],
    ["Bore Diameter", fmtDual(f.boreMm, metric)],
    ["Raised Face Diameter", fmtDual(f.rfMm, metric)],
    ["Number of Bolt Holes", `${f.nBolts}`],
    ["Bolt Hole Diameter", fmtDual(f.holeMm, metric, 3)],
    ["Bolt / Stud Size", f.bolt],
    ["Bolt Circle Diameter", fmtDual(f.bcMm, metric)],
    ["Stud Length", f.studLen],
    ["Nut (heavy hex)", f.nutAf],
    ["Wrench", f.wrench],
    ["Make-up Torque (per bolt)", f.torque ? `≈ ${f.torque.toLocaleString()} ft·lb (lubricated B7)` : "use mfr torque chart"],
  ];
}

const KEYIFY = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function SpecRow({ label, value }) {
  const k = KEYIFY(label);
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b last:border-b-0 group" style={{ borderColor: "var(--rs-border)" }}>
      <span className="text-[11.5px] text-[var(--rs-text-3)] shrink-0">{label}</span>
      <span className="flex items-center gap-1.5 min-w-0">
        <span className="text-[12.5px] font-semibold text-right" data-testid={`ff-field-${k}`}>{value}</span>
        <button
          type="button"
          data-testid={`ff-copy-${k}`}
          aria-label={`Copy ${label}`}
          onClick={() => copyText(`${label}: ${value}`, label)}
          className="p-1 -m-0.5 opacity-40 hover:opacity-100 shrink-0"
        >
          <Copy className="w-3 h-3" />
        </button>
      </span>
    </div>
  );
}

function MatchChip({ f, onSelect, testId, active }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onSelect(f)}
      className="w-full text-left px-3 py-2.5 rounded-md border transition-colors"
      style={{
        borderColor: active ? "var(--rs-amber)" : "var(--rs-border)",
        background: active ? "rgba(255,179,0,0.08)" : "var(--rs-surface)",
      }}
    >
      <span className="text-[13px] font-semibold">{f.size} — {f.psi.toLocaleString()} psi</span>
      <span className="ml-2 font-mono-rs text-[10.5px]" style={{ color: "var(--rs-amber)" }}>{f.ringLabel}</span>
      <span className="ml-2 text-[10.5px] text-[var(--rs-text-3)]">Type {f.type}</span>
    </button>
  );
}

function FlangeFinderInner() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [metric, setMetric] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [favs, setFavs] = useState(() => getFlangeFavs());

  const matches = useMemo(() => searchFlanges(query).slice(0, 8), [query]);
  const favFlanges = useMemo(() => FLANGES.filter((f) => favs.includes(f.key)), [favs]);
  const rows = selected ? buildSpecRows(selected, metric) : [];

  const onSelect = (f) => { setSelected(f); };
  const exportPdf = async () => {
    if (pdfBusy || !selected) return;
    setPdfBusy(true);
    try {
      const { exportFlangePdf } = await import("@/lib/flangeFinderPdf");
      exportFlangePdf(selected, rows);
      toast.success("PDF exported");
    } catch (err) {
      toast.error("PDF export failed on this device — opening print view instead");
      try { window.print(); } catch { /* no print support */ }
    } finally {
      setPdfBusy(false);
    }
  };
  const copyAll = () => {
    const text = [`${selected.size} — ${selected.psi.toLocaleString()} psi (API 6A Type ${selected.type})`,
      ...rows.map(([l, v]) => `${l}: ${v}`)].join("\n");
    copyText(text, "All specs");
  };

  return (
    <div className="flex flex-col gap-4" data-testid="flange-finder">
      <section>
        <button
          type="button"
          data-testid="flange-back-btn"
          onClick={() => navigate("/categories")}
          className="inline-flex items-center gap-1 text-[11px] text-[var(--rs-text-3)] hover:text-[var(--rs-amber)]"
        >
          <ArrowLeft className="w-3 h-3" /> Calculators
        </button>
        <h2 className="font-heading font-black text-2xl tracking-tight mt-1 flex items-center gap-2">
          <Ruler className="w-5 h-5" style={{ color: "var(--rs-amber)" }} />
          Smart API Flange Finder
        </h2>
        <p className="text-[12px] text-[var(--rs-text-2)] mt-1">
          API 6A Type 6B (3K/5K) & 6BX (10K/15K/20K) · 1-13/16" – 26-3/4". Search by size, pressure, ring, bolt, OD or bore.
        </p>
      </section>

      {/* Search */}
      <section className="card-rs p-3.5">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rs-text-3)]" />
          <input
            data-testid="ff-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={'e.g. 4-1/16 10K · BX-155 · RX-24 · 1-1/8" bolt'}
            className="w-full h-11 rounded-md border bg-[var(--rs-surface)] pl-9 pr-9"
            style={{ borderColor: "var(--rs-border)", fontSize: 16 }}
            inputMode="search"
            autoComplete="off"
          />
          {query && (
            <button type="button" data-testid="ff-clear-search" onClick={() => setQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-[var(--rs-text-3)]" aria-label="Clear search">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {query && (
          <div className="mt-2.5 flex flex-col gap-1.5" data-testid="ff-suggestions">
            {matches.length === 0 && (
              <p className="text-[12px] text-[var(--rs-text-3)] px-1" data-testid="ff-no-results">
                No standard flange matches "{query}".
              </p>
            )}
            {matches.map((f, i) => (
              <MatchChip key={f.key} f={f} onSelect={onSelect} testId={`ff-suggestion-${i}`} active={selected?.key === f.key} />
            ))}
          </div>
        )}
      </section>

      {/* Favorites quick reference */}
      {favFlanges.length > 0 && (
        <section data-testid="ff-favorites">
          <h3 className="text-[11px] label-up text-[var(--rs-text-3)] mb-2 flex items-center gap-1.5">
            <Star className="w-3.5 h-3.5" style={{ color: "var(--rs-amber)", fill: "var(--rs-amber)" }} />
            Quick reference
          </h3>
          <div className="flex flex-wrap gap-2">
            {favFlanges.map((f, i) => (
              <button
                key={f.key}
                type="button"
                data-testid={`ff-fav-chip-${i}`}
                onClick={() => onSelect(f)}
                className="px-3 py-1.5 rounded-full border text-[12px] font-semibold transition-colors"
                style={{
                  borderColor: selected?.key === f.key ? "var(--rs-amber)" : "var(--rs-border)",
                  background: "rgba(255,179,0,0.07)",
                }}
              >
                {f.size} · {f.psi / 1000}K
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Selected flange */}
      {selected && (
        <section className="card-rs p-3.5" data-testid="ff-result-card">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div>
              <div className="font-heading font-black text-lg leading-tight" data-testid="ff-selected-title">
                {selected.size} — {selected.psi.toLocaleString()} psi
              </div>
              <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5">
                API 6A Type {selected.type} · {selected.ringLabel} · {selected.bar} bar
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                data-testid="ff-unit-toggle"
                onClick={() => setMetric((m) => !m)}
                className="font-mono-rs text-[10px] tracking-[0.14em] uppercase px-2.5 py-1 rounded border"
                style={{ borderColor: "var(--rs-border)", color: "var(--rs-amber)" }}
              >
                {metric ? "mm → in" : "in → mm"}
              </button>
              <button
                type="button"
                data-testid="ff-fav-btn"
                aria-label={favs.includes(selected.key) ? "Remove favorite" : "Save favorite"}
                onClick={() => setFavs(toggleFlangeFav(selected.key))}
                className="p-1"
              >
                <Star className="w-5 h-5" style={{
                  color: "var(--rs-amber)",
                  fill: favs.includes(selected.key) ? "var(--rs-amber)" : "none",
                }} />
              </button>
            </div>
          </div>

          <FlangeDiagram f={selected} metricFirst={metric} />

          <h4 className="text-[11px] label-up text-[var(--rs-text-3)] mt-4 mb-1.5">Full specifications</h4>
          <div>
            {rows.map(([label, value]) => (
              <SpecRow key={label} label={label} value={value} />
            ))}
          </div>

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              data-testid="ff-export-pdf"
              onClick={exportPdf}
              disabled={pdfBusy}
              className="flex-1 h-10 rounded-md font-semibold text-[13px] inline-flex items-center justify-center gap-1.5 disabled:opacity-70"
              style={{ background: "var(--rs-amber)", color: "#141419" }}
            >
              {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" data-testid="ff-pdf-spinner" /> : <FileDown className="w-4 h-4" />}
              {pdfBusy ? "Generating…" : "Export PDF"}
            </button>
            <button
              type="button"
              data-testid="ff-copy-all"
              onClick={copyAll}
              className="flex-1 h-10 rounded-md font-semibold text-[13px] border inline-flex items-center justify-center gap-1.5"
              style={{ borderColor: "var(--rs-border)" }}
            >
              <Copy className="w-4 h-4" /> Copy All
            </button>
          </div>
        </section>
      )}

      {!selected && !query && (
        <p className="text-[12px] text-[var(--rs-text-3)] px-1" data-testid="ff-empty-hint">
          Type a flange size ("4-1/16"), pressure ("10K"), ring number ("BX-155"), bolt size or OD to find a flange.
          {favFlanges.length === 0 && " Star a flange to pin it here for quick reference."}
        </p>
      )}

      <p className="text-[10.5px] leading-relaxed text-[var(--rs-text-3)] px-1 pb-16" data-testid="ff-disclaimer">
        Reference per API 6A dimension tables (6B: thickness C, hub OD X, groove pitch P/width F/depth E;
        6BX: thickness T, hub base OD J1, groove OD G/depth Q). 6BX groove width computed from the BX ring
        section and 23° groove wall geometry. Ring dimensions per ASME B16.20. Stud sizes and lengths per the
        API 6A flange bolt chart; nut/wrench sizes are heavy-hex across-flats. Make-up torque is the final-pass
        value for lubricated ASTM A193 B7/B16 studs. Always verify against the manufacturer's drawing, company
        torque chart, and current API 6A edition before make-up. Values rounded.
      </p>

      {/* Floating back button — always visible on scroll */}
      <button
        type="button"
        data-testid="ff-floating-back"
        onClick={() => navigate("/categories")}
        className="fixed left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-1.5 px-5 h-10 rounded-full font-semibold text-[13px]"
        style={{
          bottom: "calc(72px + env(safe-area-inset-bottom, 0px))",
          background: "var(--rs-amber)",
          color: "#141419",
          boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
        }}
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
    </div>
  );
}

export default function FlangeFinder() {
  return (
    <FinderErrorBoundary>
      <FlangeFinderInner />
    </FinderErrorBoundary>
  );
}
